import React, { useEffect, useRef } from 'react';
import './style.scss';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { Device } from 'services/Native/Device';

type Position = { x: number; y: number };

const lineWidth = 2;
const lineCap = 'round';
const strokeStyle = '#000000';

type Props = {
  onSave?: (uri: string) => void;
  onCancel?: () => void;
};

export const PatientSignatureCanvasResultListener: Props = {
  onSave: undefined,
  onCancel: undefined,
};

export default () => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const position = useRef<Position>();
  const shouldDraw = useRef<boolean>(false);
  const canvasContext = useRef<CanvasRenderingContext2D | null | undefined>();

  const isNativeMobile = Device.isMobile();

  /*
   * Disable weird bounce-drag effect on safari iOS that prevents signature from being drawn
   */
  useEffect(() => {
    const cb = (e: TouchEvent) => e.preventDefault();

    document.ontouchmove = cb;

    return () => {
      document.ontouchmove = undefined;
    };
  }, []);

  async function onClickSave() {
    let uri = canvas.current?.toDataURL('image/png');
    if (uri) {
      uri = await cropImage2(uri);
    }
    if (uri && isNativeMobile) {
      uri = await rotateBase64Image90deg(uri, false);
    }

    if (uri) {
      PatientSignatureCanvasResultListener.onSave?.(uri);
    }
  }

  function onClickCancel() {
    PatientSignatureCanvasResultListener.onCancel?.();
  }

  function onClickClear() {
    canvasContext.current?.clearRect(
      0,
      0,
      canvas.current!.clientWidth,
      canvas.current!.clientHeight,
    );
  }

  useEffect(() => {
    const ctx = canvas.current?.getContext('2d');
    canvasContext.current = ctx;

    if (!ctx) {
      return;
    }

    resize();

    function resize() {
      if (!ctx) {
        return;
      }
      ctx.canvas.width = canvas.current!.clientWidth;
      ctx.canvas.height = canvas.current!.clientHeight;
    }

    function draw(e: MouseEvent | TouchEvent) {
      const box = canvas.current!.getBoundingClientRect();
      let newPosition: { x: number; y: number } | undefined;
      if ('touches' in e) {
        const touch = e.touches.item(0);
        if (!touch) {
          newPosition = position.current;
        } else {
          newPosition = { x: touch.clientX - box.left, y: touch.clientY - box.top };
        }
      } else {
        newPosition = { x: e.clientX - box.left, y: e.clientY - box.top };
      }

      const oldPosition = position.current || newPosition;

      if (e.type === 'mousedown' || e.type === 'touchstart') {
        shouldDraw.current = true;
      } else if (e.type === 'mouseup' || e.type === 'touchend') {
        position.current = undefined;
        shouldDraw.current = false;
        return;
      }

      if (e.type === 'mousemove' && (e as MouseEvent).buttons !== 1) {
        return;
      }
      if (!ctx) {
        return;
      }
      if (!shouldDraw.current) {
        return;
      }

      ctx.beginPath();
      ctx.lineWidth = lineWidth;
      ctx.lineCap = lineCap;
      ctx.strokeStyle = strokeStyle;

      if (oldPosition) {
        ctx.moveTo(oldPosition.x, oldPosition.y);
      }
      position.current = newPosition;
      if (newPosition) {
        ctx.lineTo(newPosition.x, newPosition.y);
        ctx.stroke();
      }
    }
    canvas.current?.addEventListener('mousedown', draw);
    canvas.current?.addEventListener('mouseup', draw);
    canvas.current?.addEventListener('mousemove', draw);
    canvas.current?.addEventListener('touchstart', draw);
    canvas.current?.addEventListener('touchmove', draw);
    canvas.current?.addEventListener('touchend', draw);
    window.addEventListener('resize', resize);

    return () => {
      canvas.current?.removeEventListener('mousedown', draw);
      canvas.current?.removeEventListener('mouseup', draw);
      canvas.current?.removeEventListener('mousemove', draw);
      canvas.current?.removeEventListener('touchstart', draw);
      canvas.current?.removeEventListener('touchmove', draw);
      canvas.current?.removeEventListener('touchend', draw);
      window.removeEventListener('resize', resize);
    };
  }, []);

  let className = 'page patient-signature-canvas';
  if (isNativeMobile) {
    className += ' native-mobile';
  }

  return (
    <div className={className}>
      <div className="content">
        <div className="canvas-container">
          <canvas ref={canvas} className="signature-canvas" />
          <div className="line" />
        </div>
      </div>
      <span className="rotate-label">Rotate your phone to sign</span>
      <div className="buttons">
        <DefaultButton name="Cancel" className="light" onClick={onClickCancel} />
        <DefaultButton name="Clear" className="light" onClick={onClickClear} />
        <DefaultButton name="Submit Signature" className="blue" onClick={onClickSave} />
      </div>
    </div>
  );
};

async function rotateBase64Image90deg(base64Image: string, isClockwise: boolean) {
  // create an off-screen canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // cteate Image
  const img = new Image();

  const promise = new Promise<string | undefined>((fulfill) => {
    img.onload = () => {
      canvas.height = img.width;
      canvas.width = img.height;

      // rotate and draw source image into the off-screen canvas:
      if (isClockwise) {
        ctx!.rotate((90 * Math.PI) / 180);
        ctx!.translate(0, -canvas.width);
      } else {
        ctx!.rotate((-90 * Math.PI) / 180);
        ctx!.translate(-canvas.height, 0);
      }
      ctx!.drawImage(img, 0, 0);

      // encode image to data-uri with base64
      fulfill(canvas.toDataURL('image/png'));
    };
  });
  img.src = base64Image;
  return promise;
}

function cropImage(dataUrl: string): Promise<string> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  const image = new Image();

  return new Promise((fulfill) => {
    image.onload = () => {
      canvas.width = image.width;
      canvas.height = image.height;

      ctx.drawImage(image, 0, 0);

      // const canvas = ctx.canvas
      let w = canvas.width;
      let h = canvas.height;
      const pix: { x: number[]; y: number[] } = { x: [], y: [] };
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let x: number;
      let y: number;
      let index: number;

      for (y = 0; y < h; y++) {
        for (x = 0; x < w; x++) {
          index = (y * w + x) * 4;
          if (imageData.data[index + 3]! > 0) {
            pix.x.push(x);
            pix.y.push(y);
          }
        }
      }

      pix.x.sort((a, b) => a - b);
      pix.y.sort((a, b) => a - b);
      const n = pix.x.length - 1;

      w = 1 + pix.x[n]! - pix.x[0]!;
      h = 1 + pix.y[n]! - pix.y[0]!;
      const cut = ctx.getImageData(pix.x[0]!, pix.y[0]!, w, h);

      canvas.width = w;
      canvas.height = h;
      ctx.putImageData(cut, 0, 0);

      fulfill(canvas.toDataURL('image/png'));
    };
    image.src = dataUrl;
  });
}

function cropImage2(dataUrl: string): Promise<string> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  const image = new Image();

  return new Promise((fulfill) => {
    image.onload = () => {
      canvas.width = image.width;
      canvas.height = image.height;

      ctx.drawImage(image, 0, 0);

      // const canvas = ctx.canvas
      let w = canvas.width;
      let h = canvas.height;
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      let [minX, minY, maxX, maxY] = [w, h, 0, 0];

      for (let i = 3; i < imageData.data.length; i += 4) {
        const pixelIdx = i / 4;
        const x = pixelIdx % w;
        const y = Math.floor(pixelIdx / w);
        const visible = imageData.data[i]! > 0;

        if (!visible) {
          continue;
        }

        if (x < minX) {
          minX = x;
        }
        if (x > maxX) {
          maxX = x;
        }
        if (y < minY) {
          minY = y;
        }
        if (y > maxY) {
          maxY = y;
        }
      }

      const newW = maxX - minX;
      const newH = maxY - minY;

      const cut = ctx.getImageData(minX, minY, newW, newH);
      canvas.width = newW;
      canvas.height = newH;
      ctx.putImageData(cut, 0, 0);

      fulfill(canvas.toDataURL('image/png'));
    };
    image.src = dataUrl;
  });
}
